import { useCallback, useEffect } from 'react';
import { SENTRY_CONTEXT_KEYS, setSentryContext } from '@/utils/sentry';
import { useSenderCreate } from './useSenderCreate';
import { useSenderStore, useSenderStoreHydration } from './useSenderStore';
import { useWidgetConfig } from './useWidgetConfig';
import useWidgetDataAttr from './useWidgetDataAttr';

export const useSenderInit = () => {
  const { companyId } = useWidgetDataAttr();
  const { config: widgetConfig } = useWidgetConfig();
  const {
    senderId,
    actions: { setSenderId, setSignature, getStorageKey },
  } = useSenderStore();
  const hydrated = useSenderStoreHydration();

  // Create sender mutation
  const createSenderMutation = useSenderCreate();

  const autoCreateSender = useCallback(async () => {
    const storageKey = getStorageKey(
      widgetConfig?.channelIdentityId || '',
      companyId || '',
    );

    if (!widgetConfig?.channelIdentityId || !companyId) {
      return;
    }

    if (useSenderStore.persist.getOptions().name !== storageKey) {
      // To ensure two livechat widget will not have storage key collision
      useSenderStore.persist.setOptions({
        name: storageKey,
      });
      useSenderStore.persist.rehydrate();
      return;
    }

    if (!hydrated) {
      // wait until the senderId is hydrated
      return;
    }

    if (
      !senderId &&
      !createSenderMutation.isPending &&
      !createSenderMutation.isError
    ) {
      try {
        const response = await createSenderMutation.mutateAsync({
          liveChatId: widgetConfig.channelIdentityId,
          companyId,
        });
        setSenderId(response.id);
        setSignature(response.sessionSignature);
      } catch (error) {
        console.error('Failed to auto-create sender:', error);
      }
    }
  }, [
    getStorageKey,
    widgetConfig,
    companyId,
    hydrated,
    senderId,
    createSenderMutation,
    setSenderId,
    setSignature,
  ]);

  useEffect(() => {
    if (senderId) {
      setSentryContext(SENTRY_CONTEXT_KEYS.SENDER, {
        senderId,
      });
    }
  }, [senderId]);

  // Auto-create sender when widget config is available and sender doesn't exist
  useEffect(() => {
    autoCreateSender();
  }, [autoCreateSender]);
};
